import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  LEAD_STATUSES,
  OUTCOME_OPTIONS,
  QUALITY_OPTIONS,
  QUALITY_TAG_OPTIONS,
  statusBadgeClass,
  trackDealerLeadEvent,
} from "../../constants/automapDealerLeadConstants";
import { useDealerPortalIntl } from "../../hooks/useDealerPortalIntl";
import { useCurrency, useLocale } from "../../intl";
import { formatDealerLeadSource, formatDealerLeadSourceDetail } from "../../utils/dealerLeadSourceDisplay";
import {
  addDealerLeadNote,
  fetchDealerLead,
  openDealerWhatsapp,
  updateDealerLeadAssignment,
  updateDealerLeadOutcome,
  updateDealerLeadQuality,
  updateDealerLeadStatus,
} from "../../services/automapDealerLeadApi";
import { fetchDealerListing } from "../../services/automapDealerInventoryApi";
import DealerListingPublicAction from "../../components/dealer/DealerListingPublicAction";

export default function DealerLeadDetailPage() {
  const { leadId } = useParams();
  const { dealerId } = useOutletContext();
  const { locale } = useLocale();
  const { formatMoney } = useCurrency();
  const {
    t,
    formatLeadStatus,
    formatLeadTemperature,
    formatLeadType,
    formatLeadOutcome,
    formatLeadQuality,
    formatLeadQualityTag,
    translateError,
  } = useDealerPortalIntl();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [noteBody, setNoteBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState("not_set");
  const [outcomeReason, setOutcomeReason] = useState("");
  const [qualityRating, setQualityRating] = useState("");
  const [qualityTags, setQualityTags] = useState([]);
  const [salesperson, setSalesperson] = useState("");
  const [followUp, setFollowUp] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchDealerLead(dealerId, leadId);
      let listing = data.listing;
      if (listing?.id) {
        try {
          const inventory = await fetchDealerListing(dealerId, listing.id);
          listing = { ...listing, ...inventory.listing };
        } catch {
          // Lead listing enrichment is best-effort for public URL identity metadata.
        }
      }
      setDetail({ ...data, listing });
      setOutcome(data.lead.outcome || "not_set");
      setOutcomeReason(data.lead.outcomeReason || "");
      setQualityRating(data.lead.qualityRating || "");
      setQualityTags(data.lead.qualityReasonTags || []);
      setSalesperson(data.lead.assignedSalespersonName || "");
      setFollowUp(data.lead.nextFollowUpAt ? data.lead.nextFollowUpAt.slice(0, 16) : "");
      trackDealerLeadEvent("dealer_lead_opened", { leadId, dealerId });
    } catch (err) {
      setError(translateError(err.message, "loadLead"));
    } finally {
      setLoading(false);
    }
  }, [dealerId, leadId, translateError]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <p className="dealer-state-box">{t("leadDetail.loading")}</p>;
  if (!detail?.lead) {
    return (
      <div className="dealer-state-box">
        <p>{error || t("leadDetail.notFound")}</p>
        <Link to="/v/automap/dealer/leads">{t("leadDetail.backToInbox")}</Link>
      </div>
    );
  }

  const { lead, notes, events, listing, dealerWhatsappUrl } = detail;

  const saveStatus = async (status) => {
    setBusy(true);
    try {
      await updateDealerLeadStatus(dealerId, leadId, { status });
      trackDealerLeadEvent("dealer_lead_status_changed", { leadId, status });
      await load();
    } catch (err) {
      setError(t("leadDetail.errors.updateStatus"));
    } finally {
      setBusy(false);
    }
  };

  const saveNote = async (event) => {
    event.preventDefault();
    if (!noteBody.trim()) return;
    setBusy(true);
    try {
      await addDealerLeadNote(dealerId, leadId, noteBody.trim());
      trackDealerLeadEvent("dealer_lead_note_added", { leadId });
      setNoteBody("");
      await load();
    } catch (err) {
      setError(t("leadDetail.errors.saveNote"));
    } finally {
      setBusy(false);
    }
  };

  const saveOutcome = async () => {
    setBusy(true);
    try {
      await updateDealerLeadOutcome(dealerId, leadId, {
        outcome,
        outcomeReason: outcomeReason.trim() || null,
        soldListingId: listing?.id || lead.listingId,
      });
      trackDealerLeadEvent("dealer_lead_outcome_recorded", { leadId, outcome });
      await load();
    } catch (err) {
      setError(t("leadDetail.errors.saveOutcome"));
    } finally {
      setBusy(false);
    }
  };

  const saveQuality = async () => {
    if (!qualityRating) return;
    setBusy(true);
    try {
      await updateDealerLeadQuality(dealerId, leadId, {
        qualityRating,
        qualityReasonTags: qualityTags,
      });
      trackDealerLeadEvent("dealer_lead_quality_rated", { leadId, qualityRating });
      await load();
    } catch (err) {
      setError(t("leadDetail.errors.saveRating"));
    } finally {
      setBusy(false);
    }
  };

  const saveAssignment = async () => {
    setBusy(true);
    try {
      await updateDealerLeadAssignment(dealerId, leadId, {
        assignedSalespersonName: salesperson.trim() || null,
        nextFollowUpAt: followUp ? new Date(followUp).toISOString() : null,
      });
      await load();
    } catch (err) {
      setError(t("leadDetail.errors.saveAssignment"));
    } finally {
      setBusy(false);
    }
  };

  const handleWhatsapp = () => {
    if (!dealerWhatsappUrl) {
      setError(t("leadDetail.errors.invalidWhatsapp"));
      return;
    }
    openDealerWhatsapp(dealerWhatsappUrl, {
      dealerId,
      leadId,
      onOpened: (data) => {
        trackDealerLeadEvent("dealer_lead_whatsapp_opened", { leadId });
        setDetail(data);
      },
    });
  };

  return (
    <div className="dealer-lead-detail">
      <div className="import-page-header">
        <div>
          <h2>{lead.buyerName || t("leadDetail.unnamedLead")}</h2>
          <p className="muted">
            <span className={`lead-badge ${statusBadgeClass(lead.status)}`}>
              {formatLeadStatus(lead.status)}
            </span>
            {lead.isHot ? <span className="lead-hot-pill">{t("leadDetail.hotPill")}</span> : null}
            · {formatLeadType(lead.leadType)} · Score {lead.leadScore}/100
          </p>
        </div>
        <div className="import-actions">
          {dealerWhatsappUrl ? (
            <button type="button" className="dealer-portal-btn primary" onClick={handleWhatsapp}>
              {t("leadDetail.openWhatsapp")}
            </button>
          ) : null}
          <button type="button" className="dealer-portal-btn" onClick={() => navigate("/v/automap/dealer/leads")}>
            {t("leadDetail.back")}
          </button>
        </div>
      </div>

      {error ? <p className="import-error">{error}</p> : null}

      <div className="dealer-lead-grid">
        <section className="dealer-state-box">
          <h3>{t("leadDetail.buyer")}</h3>
          <p><strong>{t("leadDetail.name")}:</strong> {lead.buyerName || "—"}</p>
          <p><strong>{t("common.whatsapp")}:</strong> {lead.buyerWhatsapp || "—"}</p>
          <p><strong>{t("leadDetail.email")}:</strong> {lead.buyerEmail || "—"}</p>
          <p><strong>{t("leadDetail.city")}:</strong> {[lead.buyerCity, lead.buyerNeighborhood].filter(Boolean).join(", ") || "—"}</p>
          <p><strong>{t("leadDetail.consent")}:</strong> {lead.consentToShare ? t("leadDetail.yes") : t("leadDetail.no")}</p>
        </section>

        <section className="dealer-state-box">
          <h3>{t("leadDetail.purchaseIntent")}</h3>
          <p><strong>{t("leadDetail.temperature")}:</strong> {formatLeadTemperature(lead.leadTemperature)}</p>
          <p><strong>{t("leadDetail.budget")}:</strong> {lead.budgetRange || lead.customMaxBudget || "—"}</p>
          <p><strong>{t("leadDetail.vehicleType")}:</strong> {lead.vehicleType || "—"}</p>
          <p><strong>{t("leadDetail.transmission")}:</strong> {lead.transmission || "—"}</p>
          <p><strong>{t("leadDetail.fuel")}:</strong> {lead.fuel || "—"}</p>
          <p><strong>{t("leadDetail.financing")}:</strong> {lead.financingPreference || "—"}</p>
          <p><strong>{t("leadDetail.downPayment")}:</strong> {lead.downPayment || "—"}</p>
          <p><strong>{t("leadDetail.timeline")}:</strong> {lead.purchaseTimeline || "—"}</p>
          {lead.useCases?.length ? (
            <p><strong>{t("leadDetail.useCase")}:</strong> {lead.useCases.join(", ")}</p>
          ) : null}
        </section>

        <section className="dealer-state-box">
          <h3>{t("leadDetail.vehicleListing")}</h3>
          {listing ? (
            <>
              {listing.primaryPhotoUrl ? (
                <img src={listing.primaryPhotoUrl} alt="" className="dealer-lead-listing-photo" />
              ) : null}
              <p>
                <strong>
                  {[listing.year, listing.make, listing.model, listing.version].filter(Boolean).join(" ")}
                </strong>
              </p>
              <p>{formatMoney(listing.priceCop)} · {listing.mileageKm?.toLocaleString(locale)} km</p>
              <p>{listing.transmission} · {listing.fuel} · {listing.status}</p>
              <div className="import-actions">
                {listing ? (
                  <DealerListingPublicAction
                    listing={listing}
                    locale={locale}
                    t={t}
                    dealerId={dealerId}
                    showUnavailableHint={false}
                    label={t("leadDetail.viewPublic")}
                  />
                ) : null}
                <Link className="dealer-portal-btn" to={listing.editPath}>
                  {t("leadDetail.editInventory")}
                </Link>
              </div>
            </>
          ) : (
            <p className="muted">{lead.listingTitle || t("leadDetail.noLinkedVehicle")}</p>
          )}
        </section>

        <section className="dealer-state-box">
          <h3>{t("leadDetail.source")}</h3>
          <p><strong>{formatDealerLeadSource(lead)}</strong></p>
          <p className="muted">{formatDealerLeadSourceDetail(lead)}</p>
          <p><strong>{t("leadDetail.created")}:</strong> {new Date(lead.createdAt).toLocaleString(locale)}</p>
        </section>
      </div>

      <section className="dealer-state-box">
        <h3>{t("leadDetail.workflow")}</h3>
        <label>
          {t("leadDetail.status")}
          <select value={lead.status} disabled={busy} onChange={(e) => saveStatus(e.target.value)}>
            {LEAD_STATUSES.map((value) => (
              <option key={value} value={value}>
                {formatLeadStatus(value)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("leadDetail.assignedSalesperson")}
          <input value={salesperson} onChange={(e) => setSalesperson(e.target.value)} placeholder={t("leadDetail.salespersonPlaceholder")} />
        </label>
        <label>
          {t("leadDetail.nextFollowUp")}
          <input type="datetime-local" value={followUp} onChange={(e) => setFollowUp(e.target.value)} />
        </label>
        <button type="button" className="dealer-portal-btn" disabled={busy} onClick={saveAssignment}>
          {t("leadDetail.saveAssignment")}
        </button>
      </section>

      <section className="dealer-state-box">
        <h3>{t("leadDetail.outcome")}</h3>
        <label>
          {t("leadDetail.outcome")}
          <select value={outcome} onChange={(e) => setOutcome(e.target.value)}>
            {OUTCOME_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {formatLeadOutcome(option.value)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("leadDetail.outcomeReason")}
          <textarea
            value={outcomeReason}
            onChange={(e) => setOutcomeReason(e.target.value)}
            placeholder={t("leadDetail.outcomeReasonPlaceholder")}
            rows={2}
          />
        </label>
        <button type="button" className="dealer-portal-btn primary" disabled={busy} onClick={saveOutcome}>
          {t("leadDetail.saveOutcome")}
        </button>
      </section>

      <section className="dealer-state-box">
        <h3>{t("leadDetail.leadQuality")}</h3>
        <label>
          {t("leadDetail.rating")}
          <select value={qualityRating} onChange={(e) => setQualityRating(e.target.value)}>
            <option value="">{t("leadDetail.selectPlaceholder")}</option>
            {QUALITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {formatLeadQuality(option.value)}
              </option>
            ))}
          </select>
        </label>
        <div className="lead-quality-tags">
          {QUALITY_TAG_OPTIONS.map((tag) => (
            <label key={tag.value} className="import-checkbox">
              <input
                type="checkbox"
                checked={qualityTags.includes(tag.value)}
                onChange={(e) => {
                  setQualityTags((prev) =>
                    e.target.checked ? [...prev, tag.value] : prev.filter((v) => v !== tag.value)
                  );
                }}
              />
              {formatLeadQualityTag(tag.value)}
            </label>
          ))}
        </div>
        <button type="button" className="dealer-portal-btn" disabled={busy} onClick={saveQuality}>
          {t("leadDetail.saveRating")}
        </button>
      </section>

      <section className="dealer-state-box">
        <h3>{t("leadDetail.internalNotes")}</h3>
        <form onSubmit={saveNote}>
          <textarea
            value={noteBody}
            onChange={(e) => setNoteBody(e.target.value)}
            placeholder={t("leadDetail.notePlaceholder")}
            rows={3}
          />
          <button type="submit" className="dealer-portal-btn primary" disabled={busy}>
            {t("leadDetail.addNote")}
          </button>
        </form>
        <ul className="lead-notes-timeline">
          {(notes || []).map((note) => (
            <li key={note.id}>
              <p>{note.body}</p>
              <small className="muted">
                {note.createdByLabel} · {new Date(note.createdAt).toLocaleString(locale)}
              </small>
            </li>
          ))}
        </ul>
      </section>

      <section className="dealer-state-box">
        <h3>{t("leadDetail.eventHistory")}</h3>
        <ul className="lead-events-timeline">
          {(events || []).map((event) => (
            <li key={event.id}>
              <strong>{event.eventType}</strong>
              {event.previousStatus && event.newStatus ? (
                <span> · {event.previousStatus} → {event.newStatus}</span>
              ) : null}
              {event.note ? <p>{event.note}</p> : null}
              <small className="muted">{new Date(event.createdAt).toLocaleString(locale)}</small>
            </li>
          ))}
        </ul>
      </section>

      {detail.dealerWhatsappMessage ? (
        <details className="dealer-state-box">
          <summary>{t("leadDetail.whatsappPreview")}</summary>
          <p>{detail.dealerWhatsappMessage}</p>
        </details>
      ) : null}
    </div>
  );
}
